import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useCashbackOffers, useMarketingMutation } from '@/hooks/use-marketing';
import { bpsToPercent, cashbackStatusLabel, cashbackStatusVariant, formatPeriod, type CashbackStatus } from '@/lib/marketing';

export default function AdminMarketingCashback() {
  const { t } = useTranslation();
  const { data: offers = [], isLoading } = useCashbackOffers({ adminView: true });
  const approve = useMarketingMutation('approve_cashback_offer');
  const reject = useMarketingMutation('reject_cashback_offer');

  return (
    <div className="mx-auto max-w-6xl animate-fade-in p-6">
      <h1 className="mb-1 text-xl font-semibold text-foreground">{t('mkt_cashback.title')}</h1>
      <p className="mb-4 text-sm text-muted-foreground">{t('mkt_cashback.subtitle')}</p>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_cashback.colStore')}</th>
              <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_cashback.colCashback')}</th>
              <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_cashback.colScope')}</th>
              <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_cashback.colPeriod')}</th>
              <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_cashback.colStatus')}</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t('mkt_cashback.loading')}</td></tr>
            ) : offers.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t('mkt_cashback.empty')}</td></tr>
            ) : offers.map((o: any) => (
              <tr key={o.id} className="border-b border-border last:border-0">
                <td className="p-3 text-sm font-medium text-foreground">{o.merchant?.name ?? '—'}</td>
                <td className="p-3 text-sm text-foreground">{bpsToPercent(o.cashback_bps)}</td>
                <td className="p-3 text-sm text-muted-foreground">
                  {o.scope_type === 'MERCHANT'
                    ? t('mkt_cashback.wholeStore')
                    : (o.branches ?? []).map((b: any) => b?.name).filter(Boolean).join(', ') || t('mkt_cashback.branches')}
                </td>
                <td className="p-3 text-sm text-muted-foreground">{formatPeriod(o.start_at, o.end_at)}</td>
                <td className="p-3"><Badge variant={cashbackStatusVariant[o.status as CashbackStatus]}>{cashbackStatusLabel[o.status as CashbackStatus]}</Badge></td>
                <td className="p-3">
                  {['SUBMITTED', 'DRAFT'].includes(o.status) && (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="destructive" onClick={() => reject.mutate({ id: o.id }, {
                        onSuccess: () => toast.success(t('mkt_cashback.rejected')), onError: (e: any) => toast.error(e.message),
                      })}>{t('mkt_cashback.reject')}</Button>
                      <Button size="sm" onClick={() => approve.mutate({ id: o.id }, {
                        onSuccess: () => toast.success(t('mkt_cashback.approved')), onError: (e: any) => toast.error(e.message),
                      })}>{t('mkt_cashback.approve')}</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
